import { QueryClient, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { callSvcAxiosInstance } from "@/constants";
import { callServiceEndpoints, dimensionDefinitionEndpoints } from "@/constants/endpoints";
import {
    getPersistedQualityWatchCache,
    isQualityWatchQuery,
    savePersistedQualityWatchCache,
    serializeQueryData,
} from "@/pages/performance/quality-watch/utils/quality-watch-query.utils";
import type { QualityWatch24hChartDataPoint } from "@/pages/performance/quality-watch/utils/quality-watch.types";
import {
    convertUserAvgDimensionViewResponseToChartData,
    sanitizeChartDataDates,
} from "@/pages/performance/quality-watch/utils/quality-watch.utils";

const keys = {
    averageDimensionView: (definitionIds: string[], userIds: string[], startDate: DateTime, endDate: DateTime) => [
        "user-average-dimension-view",
        [...definitionIds.sort()].join(","),
        [...userIds.sort()].join(","),
        startDate.toFormat("yyyy-MM-dd"),
        endDate.toFormat("yyyy-MM-dd"),
    ],
    callsForQualityWatch: (definitionId: string, userIds: string[], date: string, callPurposes: string[]) => [
        "calls-quality-watch",
        definitionId,
        [...userIds.sort()].join(","),
        date,
        callPurposes.length ? [...callPurposes.sort()].join(",") : "",
    ],
};

export const useAvgDimensionView = (
    definitionIds: string[],
    entities: Array<{ type: string; relationId: string }>,
    startDate: DateTime,
    endDate: DateTime,
    options?: { enabled?: boolean }
) => {
    // Extract user IDs from agent entities
    const userIds = entities.filter((e) => e.type === "agent").map((e) => e.relationId);
    const enabled = options?.enabled !== false && definitionIds.length > 0 && userIds.length > 0;

    return useQuery({
        queryKey: keys.averageDimensionView(definitionIds, userIds, startDate, endDate),
        queryFn: async () => {
            // Convert DateTime objects to string format for API
            const startDateStr = startDate.toFormat("yyyy-MM-dd");
            const endDateStr = endDate.toFormat("yyyy-MM-dd");

            // Make separate request for each user (backend only supports single userId)
            // Then merge all responses
            const requests = userIds.map((userId) =>
                callSvcAxiosInstance.get(dimensionDefinitionEndpoints.getUserAverageDimension, {
                    params: {
                        userId,
                        definitionId: definitionIds,
                        startDate: startDateStr,
                        endDate: endDateStr,
                    },
                })
            );

            // Execute all requests in parallel
            const responses = await Promise.all(requests);

            // Merge all responses into single array
            const mergedData = responses.flatMap((res) => res.data);

            // Transform to chart data
            const chartData = convertUserAvgDimensionViewResponseToChartData(mergedData);

            return sanitizeChartDataDates(startDateStr, endDateStr, chartData);
        },
        staleTime: 1000 * 60 * 60 * 2, // 2 hours - data stays fresh longer since it's persisted
        gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache much longer since it's persisted
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch if we have cached data
        enabled,
    });
};

/** BE response shape: { data: [{ callId, callTime, overallScore }] }. We use overallScore, callTime and keep callId for navigation. */
type CallDimensionRow = { callId: string; callTime: string; overallScore: number | null };

function getScore(row: CallDimensionRow): number | null {
    const raw = row.overallScore;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function mergeCallsByTime(
    entities: Array<{ id: string; relationId: string }>,
    perUserData: Array<{ userId: string; calls: CallDimensionRow[] }>
): QualityWatch24hChartDataPoint[] {
    const userIdToEntityId = new Map(entities.map((e) => [e.relationId, e.id]));
    const entries = perUserData.flatMap(({ userId, calls }) => {
        const entityId = userIdToEntityId.get(userId);
        if (!entityId) return [];
        return calls
            .filter((row) => row.callTime)
            .map((row) => ({ time: row.callTime, entityId, score: getScore(row), callId: row.callId }));
    });
    const timeMap = new Map<string, Record<string, number | string | null>>();

    entries.forEach(({ time, entityId, score, callId }) => {
        const row = timeMap.get(time) ?? {};
        row[entityId] = score;
        if (callId) row[`${entityId}__callId`] = callId;
        timeMap.set(time, row);
    });
    return [...timeMap.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([time, items]) => ({ time, ...items }));
}

/** BE: GET /calls/quality-watch?userId=&date=&definitionId=&callPurposes= (one userId per request). One request per user, then merge. */
export const useCallsForQualityWatch = (
    definitionId: string,
    entities: Array<{ id: string; type: string; relationId: string }>,
    date: DateTime,
    options?: { enabled?: boolean; callPurposes?: string[] }
) => {
    const userIds = useMemo(() => entities.filter((e) => e.type === "agent").map((e) => e.relationId), [entities]);
    const dateStr = useMemo(() => date.toFormat("yyyy-MM-dd"), [date]);
    const callPurposes = options?.callPurposes ?? [];
    const enabled = useMemo(
        () => options?.enabled !== false && !!definitionId && userIds.length > 0,
        [options?.enabled, definitionId, userIds.length]
    );

    return useQuery({
        queryKey: keys.callsForQualityWatch(definitionId, userIds, dateStr, callPurposes),
        queryFn: async () => {
            const params: Record<string, string> = { userId: "", date: dateStr, definitionId };
            if (callPurposes.length > 0) {
                params.callPurposes = callPurposes.join(",");
            }
            const responses = await Promise.all(
                userIds.map((userId) =>
                    callSvcAxiosInstance.get<{ data: CallDimensionRow[] }>(
                        callServiceEndpoints.getCallsForQualityWatchChart,
                        { params: { ...params, userId } }
                    )
                )
            );
            const perUserData = userIds.map((userId, i) => ({
                userId,
                calls: responses[i].data?.data ?? [],
            }));
            return mergeCallsByTime(entities, perUserData);
        },
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled,
    });
};

/**
 * Setup Quality Watch query persistence for the QueryClient
 * This handles persisting and restoring Quality Watch queries to/from localStorage
 */
export function setupQualityWatchQueryPersistence(queryClient: QueryClient): void {
    // Restore persisted Quality Watch queries on startup
    const persistedCache = getPersistedQualityWatchCache();
    if (Object.keys(persistedCache).length > 0) {
        Object.entries(persistedCache).forEach(([key, value]) => {
            try {
                const queryKey = JSON.parse(key);
                if (
                    isQualityWatchQuery(queryKey) &&
                    value &&
                    typeof value === "object" &&
                    "data" in value &&
                    "dataUpdatedAt" in value &&
                    typeof value.dataUpdatedAt === "number"
                ) {
                    const cacheEntry = value as { data: unknown; dataUpdatedAt: number; status: string };
                    // Set the query data in the cache
                    queryClient.setQueryData(queryKey, cacheEntry.data, {
                        updatedAt: cacheEntry.dataUpdatedAt,
                    });
                }
            } catch (error) {
                console.warn("Failed to restore persisted query:", key, error);
            }
        });
    }

    // Subscribe to cache updates to persist Quality Watch queries when they change
    queryClient.getQueryCache().subscribe((event) => {
        if (event?.type === "updated" && event.query) {
            const query = event.query;
            if (isQualityWatchQuery(query.queryKey) && query.state.status === "success" && query.state.data) {
                const cache = getPersistedQualityWatchCache();
                const serializedData = serializeQueryData(query.state.data);
                if (serializedData !== null) {
                    const cacheKey = JSON.stringify(query.queryKey);
                    cache[cacheKey] = {
                        data: serializedData,
                        dataUpdatedAt: query.state.dataUpdatedAt || DateTime.now().toMillis(),
                        status: "success",
                    };
                    savePersistedQualityWatchCache(cache);
                }
            }
        }
    });
}
